import { Card, CardContent, Stack, Typography, useTheme } from "@mui/material";
import { useEffect} from "react";
import { useTranslation } from "react-i18next";
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { ContentContainer } from "../content-container/ContentContainer";
import { ContentSection } from "../content-section/ContentSection";
import SideSheetFilterInput from "../side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import VirtualizedList from "../virtualized-list/VirtualizedList";
import { useAlarmSetpointTestDisableState } from "./AlarmSetpointTestDisableState";
import { ISummaryUsageDTO } from "../../services/setpoint-administration-service/Models";
import { AlarmSetpointTestDisableFormValuesType, SetPointTestDisableActionEnum, actionItems } from "./AlarmSetpointTestDisableUtils";
import ActionMenu from "../action-menu/ActionMenu";
import { BaseActions } from "../action-menu/ActionMenuItem";
import FormProperty from "../forms/form-property/FormProperty";
import FormCheckbox from "../forms/form-checkbox/FormCheckbox";
import { useFieldArray, useFormContext } from "react-hook-form";
import { isMobile, isTablet } from "react-device-detect";
import SideSheet from "../side-sheet/side-sheet/SideSheet";
import { format } from "date-fns";
import _ from "lodash";
import SetpointAdministrationDataService from "../../services/setpoint-administration-service/SetpointAdministrationService";
import { RestError } from "../../services/rest-service/RestService";
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import AlarmSetpointDisableDialog, { bindAlarmSetpointDisableDialog, useAlarmSetpointDisableDialogState } from "../alarm-setpoint-disable-dialog/AlarmSetpointDisableDialog";
import { SetpointUsageItem } from "../admin-setpoint-list/SetpointUsageItem";
import { contains } from "../../common/utilities/Contains";

export type SetpointTestDisableListProps = {
    groupID: number;
    name: 'devices' | 'wells' | 'tanks' | 'tankBatteries';
    setpointName: string;
    alarmColor: string;
    acknowledgeColor: string;
    openSideSheet: boolean;
    onCloseSideSheet: () => void;
    viewOptionState: ViewOptionStateEnum;
    setViewOptionState: (value: ViewOptionStateEnum) => void;
    visible: boolean;
    onDisabled: () => void;
};

export default function SetpointTestDisableList({
    groupID,
    name,
    setpointName,
    alarmColor,
    acknowledgeColor,
    viewOptionState,
    setViewOptionState,
    openSideSheet,
    onCloseSideSheet,
    visible,
    onDisabled
}: SetpointTestDisableListProps) {

    const isPhone = isMobile && !isTablet;
    const { t } = useTranslation();
    const { confirm, acknowledge } = useConfirmation();
    const [,setAppNotificationState] = useAppNotificationContext();
    const theme = useTheme();

    const disableDialog = useAlarmSetpointDisableDialogState();

    const {state,
        setFilterText,
        resetState
    } = useAlarmSetpointTestDisableState();    

    const {
        getValues,
        //watch,
        trigger,
        control,
        //reset,
        setValue,
        //resetField
    } = useFormContext<AlarmSetpointTestDisableFormValuesType>();        

    const { fields: usageObjects } = useFieldArray({
        control,
        name: `${name}`,
        keyName: 'fieldKey'
    });

    async function onDisable(item: ISummaryUsageDTO) {

        const result = await disableDialog.open({
            tagID: item.tagID,
            setpointID: item.setPointID
        });

        if (result.reason === 'ok') {
            onDisabled();
        }
    }

    async function onTestNotifications(item: ISummaryUsageDTO) {

        const result = await confirm({
            dialogTitle: t("setpointAdmin.test.title"),
            confirmationText: t("setpointAdmin.test.text"),
            yesButtonText: t("buttons.yes"),
            noButtonText: t("buttons.no"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        });

        if (result ==="yes") {

            try {
                await SetpointAdministrationDataService.testSetpoint(groupID, item.setPointID, item.tagID);

                acknowledge(t('setpointAdmin.test.success', {name: setpointName}));

            } catch (error) {
                const restError = error as RestError;
                
                setAppNotificationState({
                    notificationType: AppNotificationTypeEnum.Error,
                    show: true,
                    error: restError
                });
            }
        }
    }

    function onAction(action: SetPointTestDisableActionEnum | BaseActions, item: ISummaryUsageDTO) {

        switch(action) {
            case SetPointTestDisableActionEnum.Test:
                onTestNotifications(item);
                break;

            case SetPointTestDisableActionEnum.Disable:
                onDisable(item);
                break;
        }

    }

    function onNotifyChanged(index: number) {

        const notifyOnAlarm = getValues(`${name}.${index}.notifyOnAlarm`);

        if (!notifyOnAlarm) {
            setValue(`${name}.${index}.notifyOnNormal`, false);
        }

        trigger();
    }

    function onFilterItem(item: ISummaryUsageDTO): boolean {

         return !!contains(item.tagObjectName, state.filterText);
    }

    function getDisabledText(item: ISummaryUsageDTO): string {
        return t("alarmSetpointList.disabledUntilText", {
            timestamp: format(new Date(item.disabledUntil), "P pp")
        });
    }

    function renderItem(_index: number, item: ISummaryUsageDTO) {

        let actions = _.cloneDeep(actionItems);

        if (item.inAlarm) {
            actions = actions.filter(o => o.action !== SetPointTestDisableActionEnum.Disable);
        }

        actions.forEach(o => o.onClick = () => onAction(o.action, item));

        const ndx = usageObjects.findIndex(o => o.tagID === item.tagID);
        const notifyOnAlarm = getValues(`${name}.${ndx}.notifyOnAlarm`)

        return (
            <Card variant="outlined" key={item.tagID}>
                <CardContent>

                    <Stack
                        direction='row'
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between'
                        }}>

                        <SetpointUsageItem
                            name=''
                            grouped={false}
                            item={item}
                            alarmColor={alarmColor}
                            acknowledgeColor={acknowledgeColor}
                            sx={{
                                marginTop: 'auto',
                                marginBottom: 'auto'
                            }}
                            size="medium" />


                        <ActionMenu actionItems={actions} />

                    </Stack>

                    {item.isDisabled && <Stack
                        direction='row'
                        sx={{
                            display: 'flex',
                            paddingLeft: theme.spacing(5)
                        }}>
                    <Typography variant="body2" style={{ color: "green", fontStyle: "italic" }}>
                        {getDisabledText(item)}
                    </Typography>


                    </Stack>}

                    <Stack
                        direction='row'
                        sx={{
                            display: 'flex',
                            paddingLeft: theme.spacing(5)
                        }}>

                        <FormProperty sx={{paddingBottom: 0}}>
                            <FormCheckbox
                                name={`${name}.${ndx}.notifyOnAlarm`}
                                label={t('setpointAdmin.alarmNotify')}
                                onChange={() => onNotifyChanged(ndx)}
                                sx={{ 
                                    marginRight: 0,
                                    marginTop: 0,
                                    '& > *': {
                                        fontSize: '.9rem'
                                    }
                                }} />

                        </FormProperty>

                        <FormProperty sx={{paddingBottom: 0}}>

                            <FormCheckbox 
                                name={`${name}.${ndx}.notifyOnNormal`}
                                label={t('setpointAdmin.clearNotify')}
                                onChange={() => onNotifyChanged(ndx)}
                                disabled={!notifyOnAlarm}
                                sx={{ 
                                    marginRight: 0,
                                    marginTop: 0,
                                    '& > *': {
                                        fontSize: '.9rem'
                                    }
                                }} />
                        </FormProperty>
                    </Stack>
               </CardContent>
            </Card>
        );
    }

    useEffect(() => {

        if (visible && viewOptionState !== state.viewOptionState) {
            setViewOptionState(state.viewOptionState);
        }
    }, [
        viewOptionState,
        state.viewOptionState,
        setViewOptionState,
        visible
    ]);

    return (

        visible ?
            <>
            <ContentContainer>
                <ContentSection>

                    <VirtualizedList
                        items={usageObjects} 
                        renderItem={renderItem}
                        filter={onFilterItem}
                    />

                </ContentSection>

                <SideSheet
                    title={t("options")}
                    open={openSideSheet ?? false}
                    hideCloseButton={!isPhone}
                    onClose={() => onCloseSideSheet && onCloseSideSheet()}
                    onReset={resetState}
                >
                    <SideSheetFilterInput
                        label={t("setpointAdmin.filterBy")}
                        value={state.filterText}
                        onChange={(value: string) => {
                            setFilterText(value);
                        }}
                    />

                </SideSheet>

                
            </ContentContainer>

            <AlarmSetpointDisableDialog {...bindAlarmSetpointDisableDialog(disableDialog)} />
            </>
        :
        null
    );
}
